/**
 * Helper functions for table operations
 */

/**
 * Generate cache key for table data
 */
export const generateCacheKey = (resource, filters, sort, page) => {
  return `table_${resource}_${JSON.stringify({ filters, sort, page })}`;
};

/**
 * Format data for CSV export
 */
export const formatDataForExport = (data, columns) => {
  return data.map(row => {
    const formattedRow = {};
    columns.forEach(col => {
      if (col.selector) {
        const value = typeof col.selector === 'function' ? col.selector(row) : row[col.selector];
        formattedRow[col.name] = col.format ? col.format(row) : value;
      }
    });
    return formattedRow;
  });
};

/**
 * Get CSV headers from columns
 */
export const getCSVHeaders = columns => {
  return columns
    .filter(col => !col.omit && col.selector)
    .map(col => ({
      label: col.name,
      key: col.id || col.name,
    }));
};

/**
 * Calculate summary statistics
 */
export const calculateSummary = (data, summaryConfig) => {
  if (!summaryConfig || !data || data.length === 0) return {};

  const summary = {};
  summaryConfig.forEach(config => {
    switch (config.type) {
      case 'count':
        summary[config.key] = data.length;
        break;
      case 'sum':
        summary[config.key] = data.reduce((acc, row) => acc + (Number(row[config.field]) || 0), 0);
        break;
      case 'avg': {
        const sum = data.reduce((acc, row) => acc + (Number(row[config.field]) || 0), 0);
        summary[config.key] = sum / data.length;
        break;
      }
      case 'countBy':
        summary[config.key] = data.filter(row => row[config.field] === config.value).length;
        break;
      default:
        break;
    }
  });

  return summary;
};

/**
 * Apply local filters to data (for client-side filtering)
 */
export const applyLocalFilters = (data, filters) => {
  if (!filters || Object.keys(filters).length === 0) return data;

  return data.filter(row => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value || value === '') return true;

      const rowValue = row[key];

      // Handle different filter types
      if (Array.isArray(value)) {
        return value.includes(rowValue);
      }

      if (typeof value === 'object' && value.start && value.end) {
        // Date range
        const rowDate = new Date(rowValue);
        return rowDate >= new Date(value.start) && rowDate <= new Date(value.end);
      }

      // Text search
      return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
    });
  });
};

/**
 * Sort data locally
 */
export const sortData = (data, sortField, sortOrder) => {
  if (!sortField) return data;

  return [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];

    if (aVal === bVal) return 0;

    const comparison = aVal > bVal ? 1 : -1;
    return sortOrder === 'asc' ? comparison : -comparison;
  });
};

/**
 * Paginate data locally
 */
export const paginateData = (data, page, perPage) => {
  const start = (page - 1) * perPage;
  const end = start + perPage;
  return data.slice(start, end);
};
